/** @format */

import React, { useState, useEffect } from "react";

import { useLocale } from "../../../i18n/hooks";
import { useTranslation } from "react-i18next";
import { ReactComponent as XCircle } from "bootstrap-icons/icons/x-circle.svg";
import { useParams } from "react-router-dom";
import {
  QualificationId,
  ResourceReference,
  SpecItemData,
  UserResourceReference,
} from "../../../types";
import "./ResourcesFilter.scss";
import { QueryProps } from "../resourceIndexUI/ResourcesIndexUI";
import TagFilter from "./tagFilter/TagFilter";
import TypesFilter from "./typesFilter/TypesFilter";
import QualificationFilter from "./qualificationFilter/QualificationFilter";
import SpecificationFilter from "./specificationFilter/SpecificationFilter";
import LevelFilter from "./levelFilter/LevelFilter";
import CatFilter from "./catFilter/CatFilter";
import StatusFilter from "./statusFilter/StatusFilter";

export type AvailableFilters =
  | "qualification"
  | "spec"
  | "level"
  | "type"
  | "cat"
  | "tag"
  | "status";

type Props = {
  className?: string;
  query: QueryProps;
  handleQueryItemClick: (
    thisValue: string,
    thisQueryType: keyof QueryProps,
  ) => void;
  handleClearClick: () => void;
  resources: ResourceReference[] | UserResourceReference[];
  specItems: SpecItemData | undefined;
  noSpecItems: boolean | undefined;
  activeFilters: AvailableFilters[];
};

type ParamAggregate = { title: string; count: number };

export type AllParams = {
  types: ParamAggregate[];
  tags: string[];
  cats: ParamAggregate[];
  level: ParamAggregate[];
  qualification: (Omit<ParamAggregate, "title"> & { title: QualificationId })[];
  specItem: ParamAggregate[];
};

const Filters: React.FC<Props> = ({
  className,
  query,
  handleQueryItemClick,
  handleClearClick,
  resources,
  specItems,
  noSpecItems,
  activeFilters,
}) => {
  const [allParams, setAllParams] = useState<AllParams>({
    types: [],
    tags: [],
    cats: [],
    level: [],
    qualification: [],
    specItem: [],
  });
  const [ignoredTags, setIgnoredTags] = useState<string[]>([]);
  const locale = useLocale();
  const { t: _ } = useTranslation();

  const { subject } = useParams();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setIsLoading(!resources);
  }, [resources]);

  useEffect(() => {
    const _ignoredTags: string[] = [
      "gcse",
      "tgau",
      "as",
      "a level",
      "a-level",
      "ug",
      "safon uwch",
    ];
    const _allTags: string[] = [];
    const _allTypes: ParamAggregate[] = [];
    const _allLevels: ParamAggregate[] = [];
    const _allSpecs: ParamAggregate[] = [];
    const _allCats: ParamAggregate[] = [];
    const _allQuals: (Omit<ParamAggregate, "title"> & {
      title: QualificationId;
    })[] = [];
    subject && _ignoredTags.push(_(subject).toLocaleLowerCase());

    if (resources && resources.length > 0) {
      resources?.forEach((resource) => {
        try {
          if (resource.Object?.levels) {
            resource.Object.levels.forEach((_level) => {
              let i = _allLevels.findIndex((l) => l.title === _level.trim());
              if (i === -1) {
                i = _allLevels.push({ title: _level.trim(), count: 0 }) - 1;
                _ignoredTags.push(_level.trim().toLocaleLowerCase());
              }
              _allLevels[i].count++;
            });
          }

          if (resource.Object?.specItem) {
            const _spec = resource.Object.specItem;

            let i = _allSpecs.findIndex((l) => l.title === _spec.trim());
            if (i === -1) {
              i = _allSpecs.push({ title: _spec.trim(), count: 0 }) - 1;
              _ignoredTags.push(_spec.trim().toLocaleLowerCase());
            }
            _allSpecs[i].count++;
          }

          resource.Object.tags &&
            resource.Object.tags.map(
              (tag) =>
                tag &&
                !_allTags.find(
                  (t) =>
                    t.toLocaleLowerCase() === tag.toLocaleLowerCase().trim(),
                ) &&
                _allTags.push(tag.trim()),
            );

          if (resource.Type) {
            let i = _allTypes.findIndex(
              (t) => t.title === resource.Type.trim(),
            );
            if (i === -1) {
              i = _allTypes.push({ title: resource.Type.trim(), count: 0 }) - 1;
              _ignoredTags.push(_(resource.Type.trim()).toLocaleLowerCase());
            }
            _allTypes[i].count++;
          }

          resource.Object.categories &&
            resource.Object.categories.forEach((_cat) => {
              let i = _allCats.findIndex((c) => c.title === _cat.trim());
              if (i === -1) {
                i = _allCats.push({ title: _cat.trim(), count: 0 }) - 1;
                _ignoredTags.push(_cat.trim().toLocaleLowerCase());
              }
              _allCats[i].count++;
            });

          if (
            resource.Qualification &&
            typeof resource.Qualification !== "undefined"
          ) {
            let qual = resource.Qualification.trim();
            let i = _allQuals.findIndex((t) => t.title === qual);
            if (i === -1) {
              i =
                _allQuals.push({ title: qual as QualificationId, count: 0 }) -
                1;
              _ignoredTags.push(_(qual).toLocaleLowerCase());
            }
            _allQuals[i].count++;
          }
        } catch {
          console.log("Failed to add resource to filter");
        }
      });
    }

    setAllParams({
      tags: _allTags,
      cats: _allCats,
      level: _allLevels,
      qualification: _allQuals,
      specItem: _allSpecs,
      types: _allTypes,
    });
    setIgnoredTags(_ignoredTags);

    return () => {
      setAllParams({
        types: [],
        tags: [],
        cats: [],
        level: [],
        qualification: [],
        specItem: [],
      });
      setIgnoredTags([]);
    };
  }, [resources, locale, _, subject]);

  return (
    <div className={`filters pt-2 ${className ?? ""}`}>
      {activeFilters.includes("status") && (
        <StatusFilter
          isLoading={isLoading}
          query={query}
          refs={resources as ResourceReference[]}
          handleQueryItemClick={handleQueryItemClick}
        />
      )}

      {activeFilters.includes("qualification") && (
        <QualificationFilter
          isLoading={isLoading}
          handleQueryItemClick={handleQueryItemClick}
          query={query}
          allParams={allParams}
          resources={resources as ResourceReference[]}
        />
      )}

      {activeFilters.includes("spec") && (
        <SpecificationFilter
          isLoading={isLoading}
          handleQueryItemClick={handleQueryItemClick}
          query={query}
          allParams={allParams}
          specItems={specItems}
          noSpecItems={noSpecItems}
          resources={resources as ResourceReference[]}
        />
      )}

      {activeFilters.includes("level") && (
        <LevelFilter
          isLoading={isLoading}
          handleQueryItemClick={handleQueryItemClick}
          query={query}
          allParams={allParams}
          resources={resources as ResourceReference[]}
        />
      )}

      {activeFilters.includes("type") && (
        <TypesFilter
          isLoading={isLoading}
          handleQueryItemClick={handleQueryItemClick}
          query={query}
          allParams={allParams}
          resources={resources as ResourceReference[]}
        />
      )}

      {activeFilters.includes("cat") && (
        <CatFilter
          resources={resources as ResourceReference[]}
          isLoading={isLoading}
          handleQueryItemClick={handleQueryItemClick}
          query={query}
          allParams={allParams}
        />
      )}

      {activeFilters.includes("tag") && (
        <TagFilter
          ignoredTags={ignoredTags}
          isLoading={isLoading}
          handleQueryItemClick={handleQueryItemClick}
          query={query}
          allParams={allParams}
        />
      )}

      {(query.types.length > 0 ||
        query.tags.length > 0 ||
        query.level ||
        query.cats.length > 0 ||
        query.qualification) && (
        <button
          onClick={() => handleClearClick()}
          className="btn btn-danger float-end"
        >
          {_("Reset filter")}&ensp;
          <XCircle />
        </button>
      )}
    </div>
  );
};

export default Filters;
